import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { ChangeDetectorRef, Component, inject, Inject, OnInit } from '@angular/core';
import { MatFormField, MatInput } from '@angular/material/input';
import { Store } from '@ngxs/store';
import { CommonModule } from '@angular/common';
import { GlobalMetaDataEntity, MetadataField } from '../global-metadata.entity';
import { MatLabel } from '@angular/material/form-field';
import { MatButton, MatIconButton } from '@angular/material/button';
import { from, Observable, switchMap } from 'rxjs';
import { GlobalMetaDataTypeEntity } from '../global-metadata-type.entity';
import { GlobalMetaDataTypeEntityService } from '../services/global-metadata-type.entity.service';
import { MatOption, MatSelect } from '@angular/material/select';
import { GlobalMetadataFactoryService } from '../services/global-metadata.factory.service';
import {
  FormDialogGlobalMetadataSelectionComponent
} from './form-dialog-global-metadata-types/selection/form-dialog-global-metadata.selection.component';
import { MetadataFieldDataType } from '../../core/types';
import { MatIcon } from '@angular/material/icon';
import {
  FormDialogGlobalMetadataDropdownComponent
} from './form-dialog-global-metadata-types/dropdown/form-dialog-global-metadata.dropdown.component';
import {
  FormDialogGlobalMetadataIconSelectionComponent
} from './form-dialog-global-metadata-types/icon-selection/form-dialog-global-metadata.icon-selection.component';
import {
  FormDialogGlobalMetadataPairedSelectionComponent
} from './form-dialog-global-metadata-types/paired-selection/form-dialog-global-metadata.paired-selection.component';

@Component({
  selector: 'pixl-form-dialog-global-metadata',
  templateUrl: './form-dialog-global-metadata.component.html',
  imports: [
    CommonModule,
    MatFormField,
    ReactiveFormsModule,
    FormsModule,
    MatDialogContent,
    MatDialogActions,
    MatLabel,
    MatInput,
    MatButton,
    MatSelect,
    MatOption,
    MatDialogTitle,
    FormDialogGlobalMetadataSelectionComponent,
    MatIcon,
    MatIconButton,
    FormDialogGlobalMetadataDropdownComponent,
    FormDialogGlobalMetadataIconSelectionComponent,
    FormDialogGlobalMetadataPairedSelectionComponent
  ],
  styleUrls: ['./form-dialog-global-metadata.component.scss']
})
export class FormDialogGlobalMetadataComponent implements OnInit {
  form: FormGroup;
  globalMetaDataTypes$!: Observable<GlobalMetaDataTypeEntity[]>;

  cdr = inject(ChangeDetectorRef);

  private store = inject(Store);
  private globalMetaDataTypeService = inject(GlobalMetaDataTypeEntityService);
  private metaFactory = inject(GlobalMetadataFactoryService);

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<FormDialogGlobalMetadataComponent>,
    @Inject(MAT_DIALOG_DATA) public data?: Partial<GlobalMetaDataEntity>
  ) {
    this.form = this.fb.group({
      name: [data?.name || '', [Validators.required]],
      description: [data?.description || ''],
      fieldConfig: this.fb.array([]),
    });
  }

  ngOnInit() {
    this.loadRelationData();

    (this.data?.fieldConfig || []).forEach(conf => {
      const group = this.metaFactory.createFieldConfGroup(conf, true);
      this.fieldConfig.push(group);
    });

    const isNew = !this.data?.id;
    if (isNew || this.fieldConfig.length === 0) {
      const initialGroup = this.metaFactory.createFieldConfGroup({
        name: '',
        label: '',
        datatype: 'String',
        subFieldConf: [],
      });
      this.fieldConfig.push(initialGroup);
    }
  }

  get fieldConfig(): FormArray<FormGroup> {
    return this.form.get('fieldConfig') as FormArray<FormGroup>;
  }

  addFieldConfig() {
    const newGroup = this.metaFactory.createFieldConfGroup({
      name: '',
      label: '',
      datatype: 'String',
      subFieldConf: [],
    } as MetadataField);

    setTimeout(() => {
      this.fieldConfig.push(newGroup);
      this.cdr.detectChanges();
    }, 0);
  }

  removeFieldConfig(index: number) {
    this.fieldConfig.removeAt(index);
  }

  public onDatatypeChange(group: FormGroup) {
    const datatype = group.get('datatype')?.value as MetadataFieldDataType;

    switch (datatype) {
      case 'DropDown':
        group.setControl('detail', this.metaFactory.createDetailGroup());
        this.clearSubFieldConf(group);
        break;

      default:
        this.clearDetail(group);
        this.clearSubFieldConf(group);
        break;
    }
  }

  public getFieldType(fc: AbstractControl): string | null {
    const datatype = fc.get('datatype')?.value as MetadataFieldDataType | undefined;

    switch (datatype) {
      case 'Selection':
        return 'Selection';
      case 'SingleSelection':
        return 'SingleSelection';
      case 'DropDown':
        return 'DropDown';
      case 'IconSelection':
        return 'IconSelection';
      case 'PairedSelection':
        return 'PairedSelection';
      default:
        return null;
    }
  }

  public clearDetail(group: FormGroup): void {
    group.setControl('detail', this.fb.control(null));
  }

  public clearSubFieldConf(group: FormGroup): void {
    const subFieldConf = group.get('subFieldConf') as FormArray;
    subFieldConf?.clear();
  }

  private loadRelationData(): void {
    const query = {
      sort: 'name',
    };
    this.globalMetaDataTypes$ = from(
      this.globalMetaDataTypeService.ensureLoaded(query)
    ).pipe(
      switchMap(() =>
        this.store.select(this.globalMetaDataTypeService.allByQuery(query))
      )
    );
  }

  save(): void {
    if (this.form.valid) {
      const formValue = this.form.getRawValue() as GlobalMetaDataEntity;

      const updatedGlobalMetaData = Object.assign(
        new GlobalMetaDataEntity(),
        { id: this.data?.id },
        formValue
      );

      this.dialogRef.close(updatedGlobalMetaData);
    }
    else {
      this.form.markAllAsTouched();
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
